import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { escapeHTML } from '../../utils/ids-xss-utils/ids-xss-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';
import IdsHideFocusMixin from '../../mixins/ids-hide-focus-mixin/ids-hide-focus-mixin';
import IdsLoadingIndicatorMixin from '../../mixins/ids-loading-indicator-mixin/ids-loading-indicator-mixin';
import IdsFieldHeightMixin from '../../mixins/ids-field-height-mixin/ids-field-height-mixin';
import IdsLabelStateMixin from '../../mixins/ids-label-state-mixin/ids-label-state-mixin';

import { type IdsColorValueStatus } from '../../utils/ids-color-utils/ids-color-utils';

import IdsElement from '../../core/ids-element';
import './ids-button-group';
import './ids-button-group-section';

import {
  BUTTON_APPEARANCE,
  BUTTON_CONTENT_ALIGNMENTS,
  BUTTON_DEFAULTS,
  BUTTON_TYPES,
  BUTTON_ATTRIBUTES,
  ICON_ALIGN_CLASSNAMES,
  baseProtoClasses
} from './ids-button-common';

import type {
  IdsButtonContentAlignment,
  IdsButtonIconAlignment,
  IdsButtonAppearance,
  IdsButtonType
} from './ids-button-common';

import styles from './ids-button.scss';
import type IdsIcon from '../ids-icon/ids-icon';
import type IdsText from '../ids-text/ids-text';
import { checkOverflow } from '../../utils/ids-dom-utils/ids-dom-utils';

const Base = IdsLabelStateMixin(
  IdsTooltipMixin(
    IdsLocaleMixin(
      IdsLoadingIndicatorMixin(
        IdsColorVariantMixin(
          IdsHideFocusMixin(
            IdsFieldHeightMixin(
              IdsEventsMixin(
                IdsElement
              )
            )
          )
        )
      )
    )
  )
);

/**
 * IDS Button Component
 * @type {IdsButton}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsColorVariantMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsTooltipMixin
 * @mixes IdsHideFocusMixin
 * @mixes IdsLoadingIndicatorMixin
 * @mixes IdsLabelStateMixin
 * @part button - the button element
 * @part icon - the icon element
 * @part text - the text element
 */
@customElement('ids-button')
@scss(styles)
export default class IdsButton extends Base {
  shouldUpdate = false;

  #isSettingInitialState = false;

  /**
   * Inherited from `IdsColorVariantMixin`
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = ['alternate', 'alternate-formatter', 'module', 'module-nav'];

  constructor() {
    super();
    Object.keys(BUTTON_DEFAULTS).forEach((prop) => {
      this.state[prop] = BUTTON_DEFAULTS[prop];
    });

    this.refreshProtoClasses();
  }

  /**
   * Button-level `connectedCallback` implementation
   * @returns {void}
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.#setInitialState();
    this.shouldUpdate = true;
    if (this.getAttribute('width')) this.width = this.getAttribute('width');
    this.cssClass = this.cssClass;
    this.#attachEventHandlers();
    this.#handleInertAttribute();

    requestAnimationFrame(() => {
      this.refreshProtoClasses();
    });
    this.mountMutationObservables();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    if (this.#mo) {
      this.#mo?.disconnect();
      this.#mo = undefined;
    }
    if (this.#audibleMo) {
      this.#audibleMo?.disconnect();
      this.#audibleMo = undefined;
    }
  }

  /**
   * Sets up mutation observers to monitor DOM changes and update ARIA text accordingly.
   * Observes both the component itself and any .audible elements for changes.
   * @returns {void}
   */
  mountMutationObservables(): void {
    const audible = this.querySelector('.audible');

    this.#mo = new MutationObserver(() => {
      this.setAriaText();
      const currentAudible = this.querySelector('.audible');

      if (currentAudible) {
        this.#audibleMo?.disconnect();

        this.#audibleMo = new MutationObserver(() => {
          this.setAriaText();
        });

        this.#audibleMo.observe(currentAudible, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
          characterData: true
        });
      }
    });

    this.#audibleMo = new MutationObserver(() => {
      this.setAriaText();
    });

    if (audible) {
      this.#audibleMo.observe(audible, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
        characterData: true
      });
    }

    this.#mo.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      characterData: true
    });
  }

  mountedCallback(): void {
    this.refreshProtoClasses();
  }

  #attachEventHandlers() {
    this.offEvent('click.internal', this.container);
    this.onEvent('click.internal', this.container, () => {
      if (this.type === 'submit') {
        const form = this.closest('form');
        if (form) {
          form.requestSubmit();
        }
      }
      if (this.appearance.includes('generative-ai')) {
        this.toggleAnimation();
      }
    });

    this.offEvent('mouseenter.button', this);
    this.onEvent('mouseenter.button', this, () => {
      const label = this.container?.getAttribute(htmlAttributes.ARIA_LABEL);
      if (label && !label.endsWith('\u200B')) {
        this.button?.setAttribute(htmlAttributes.ARIA_LIVE, 'polite');
        this.container?.setAttribute(htmlAttributes.ARIA_LABEL, `${label}\u200B`);
      }
    });

    this.offEvent('mouseleave.button', this);
    this.onEvent('mouseleave.button', this, () => {
      const label = this.container?.getAttribute(htmlAttributes.ARIA_LABEL);
      if (label?.endsWith('\u200B')) {
        this.button?.removeAttribute(htmlAttributes.ARIA_LIVE);
        this.container?.setAttribute(htmlAttributes.ARIA_LABEL, label.slice(0, -1));
      }
    });
  }

  /**
   * Toggles generative AI button active state
   * @param {boolean} isActive active flag
   */
  toggleAnimation(isActive?: boolean): void {
    if (!this.appearance.includes('generative-ai')) return;
    const currentState = this.button?.classList.contains('gen-ai-active');
    const newState = typeof isActive === 'boolean' ? isActive : !currentState;

    this.button?.classList.toggle('gen-ai-active', newState);

    if (newState && this.container) {
      const loadingDots = this.container.querySelector('.loading-dots');
      if (loadingDots) {
        loadingDots.className = `loading-dots loading-dots-${this.iconAlign || 'start'}`;
      }
    }
  }

  #setInitialState() {
    if (this.#isSettingInitialState) return;
    this.#isSettingInitialState = true;

    if (this.hasAttribute(attributes.ICON)) this.appendIcon(this.getAttribute(attributes.ICON) as string);
    if (this.hasAttribute(attributes.TEXT)) this.appendText(this.getAttribute(attributes.TEXT) as string);

    this.setAudibleText();
    this.setAriaText();

    if (this.hasAttribute(attributes.CONTENT_ALIGN)) this.setContentAlignClass(this.getAttribute(attributes.CONTENT_ALIGN));
    this.setIconAlignment();
    this.refreshProtoClasses();

    // set initial direction
    this.localeAPI.updateDirectionAttribute(this, this.localeAPI.language.name);
    this.#setupGenAIButton(this.appearance);

    this.#isSettingInitialState = false;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array<string>} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [...super.attributes, ...BUTTON_ATTRIBUTES, attributes.INERT];
  }

  /**
   * Watches for changes
   * @property {MutationObserver} mo this button component's mutation observer
   */
  #mo?: MutationObserver;

  /**
   * Watches for changes on .audible element within the button
   * @property {MutationObserver} mo audible element mutation observer
   */
  #audibleMo?: MutationObserver;

  /**
   * Figure out the classes
   * @private
   * @readonly
   * @returns {Array<string>} containing classes used to identify this button prototype
   */
  get protoClasses() {
    const textContent = this.querySelector('span:not(.audible), ids-text:not([audible])');
    const iconContent = this.iconEl;
    if (iconContent && (!textContent)) {
      return ['ids-icon-button'];
    }
    return ['ids-button'];
  }

  /**
   * Refreshes this button's prototype CSS class
   * @private
   * @returns {void}
   */
  refreshProtoClasses() {
    if (!this.button) return;
    const cl = this.button.classList;
    const newProtoClass = this.protoClasses;

    cl.remove(...baseProtoClasses);
    cl.add(...newProtoClass);

    // Check if masthead is a parent and add class
    const mastheadParent = this.closest('ids-masthead');
    if (mastheadParent && !this.closest('ids-popup')) {
      cl.add('ids-masthead-button');
    }
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template() {
    let cssClass = '';
    let protoClasses = '';
    let disabled = '';
    let tabIndex = 'tabindex="0"';
    let appearance = '';
    if (this.state?.cssClass) {
      cssClass = ` ${this.state.cssClass.join(' ')}`;
    }
    if (this.state?.disabled) {
      disabled = ` disabled="true"`;
    }
    if (this.state?.tabIndex) {
      tabIndex = `tabindex="${this.state.tabIndex}"`;
    }
    if (this.state && this.state?.appearance !== 'default' && this.state.appearance !== undefined) {
      appearance = ` btn-${this.state.appearance}`;
    }

    if (this.hasAttribute(attributes.SQUARE)) {
      cssClass += ' square';
    }

    if (this.hasAttribute(attributes.NO_PADDING)) {
      cssClass += ' no-padding';
    }

    if (this.hasAttribute(attributes.BACKGROUND_FILL)) {
      cssClass += ' background-fill';
    }

    if (this.classList.contains('ids-tab-close-button')) {
      cssClass += ' ids-tab-close-button';
    }

    if (this.classList.contains('ids-tab-cancel-button')) {
      cssClass += ' ids-tab-cancel-button';
    }

    if (this.classList.contains('ids-tab-update-button')) {
      cssClass += ' ids-tab-update-button';
    }

    if (this.classList.contains('ids-sub-tab-close-button')) {
      cssClass += ' ids-sub-tab-close-button';
    }

    cssClass += (this.overflow === 'ellipsis') ? ' ellipsis' : '';

    if (this.protoClasses.length) {
      protoClasses = `${this.protoClasses.join(' ')}`;
    }

    let alignCSS = '';
    if (this.state?.iconAlign) alignCSS = ` align-icon-${this.state?.iconAlign}`;

    return `<button part="button" type="${this.type}" class="${protoClasses}${appearance}${alignCSS}${cssClass}" ${tabIndex}${disabled}>
      <slot></slot>
      <slot name="loading-indicator"></slot>
    </button>`;
  }

  #isUpdatingHidden = false;

  /**
   * Get the tab index value
   * @returns {number} The tab index value
   */
  get tabIndex() {
    return this.#tabIndex;
  }

  /**
   * Set the tab index value
   * @param {number} value The tab index value
   */
  set tabIndex(value) {
    this.#tabIndex = value;
  }

  /**
   * Get the hidden value
   * @returns {boolean} The hidden value
   */
  get hidden() {
    return stringToBool(this.getAttribute(attributes.HIDDEN));
  }

  /**
   * Set the hidden value
   * @param {number} value The tab index value
   */
  set hidden(value: boolean) {
    this.onHiddenChange(value);
  }

  /**
   * Handles hidden attribute changes
   * @param {string} value true if hidden
   */
  onHiddenChange(value: string | boolean | null) {
    if (this.#isUpdatingHidden) return;
    this.#isUpdatingHidden = true;

    const bool = stringToBool(value);
    this.shouldUpdate = false;
    this.style.removeProperty('display');
    if (bool) {
      if (!this.hasAttribute(attributes.HIDDEN)) this.setAttribute(attributes.HIDDEN, '');
      this.classList.add('hidden');
    } else {
      if (this.hasAttribute(attributes.HIDDEN)) this.removeAttribute(attributes.HIDDEN);
      this.classList.remove('hidden');
    }
    this.shouldUpdate = true;
    this.state.hidden = bool;
    if (this.button) this.button.hidden = bool;

    this.#isUpdatingHidden = false;
  }

  /**
   * Set aria attributes
   */
  private setAriaText() {
    if (this.container) {
      const textContent = this.text?.trim();
      const isIconOnly = this.protoClasses.includes('ids-icon-button') && !textContent;
      const iconValue = this.icon?.trim();
      const ariaLabel = this.labelAudible || textContent || (isIconOnly && iconValue ? iconValue : 'Button');
      const describedBy = this.describedBy;

      if (describedBy.length > 0) this.container.setAttribute(htmlAttributes.ARIA_DESCRIBED_BY, describedBy);

      if (!this.hasAttribute(attributes.INERT)) {
        this.container.setAttribute(htmlAttributes.ARIA_LABEL, ariaLabel);
        this.container.removeAttribute(htmlAttributes.ROLE);
      }
    }
  }

  /**
   * Set the audible text for button
   */
  private setAudibleText() {
    if (!this.labelAudible) return;

    if (this.span) {
      this.container?.setAttribute(htmlAttributes.ARIA_LABEL, this.labelAudible);
      return;
    }

    let audibleSpan = this.#getAudibleSpan();
    if (!audibleSpan) {
      audibleSpan = document.createElement('span');
      audibleSpan.className = 'audible';
      this.insertAdjacentElement('afterend', audibleSpan);
    }
    audibleSpan.textContent = this.labelAudible;
  }

  /**
   * @readonly
   * @returns {HTMLButtonElement} reference to the true button element used in the Shadow Root
   */
  get button(): HTMLButtonElement | null {
    return this.shadowRoot?.querySelector('button') || null;
  }

  /**
   * @readonly
   * @returns {HTMLSpanElement} reference to the true span/text element of button used in the Shadow Root
   */
  get span(): HTMLSpanElement | null {
    return this.shadowRoot?.querySelector('slot') || null;
  }

  /**
   * Get audible span element (contains the audible text)
   * @returns {HTMLSpanElement} reference to the audible span element
   */
  #getAudibleSpan(): HTMLSpanElement | null {
    return this.querySelector('span.audible') || null;
  }

  /**
   * Toggles the compact state of the button.
   * @param {boolean | string} val true to enable compact
   */
  set compact(val: boolean | string) {
    if (!this.#isValidValue(val)) return;

    const isCompact = stringToBool(val);
    this.state.compact = isCompact;

    if (!isCompact) {
      this.removeAttribute(attributes.COMPACT);
    } else {
      this.setAttribute(attributes.COMPACT, 'true');
    }

    // Update the compact attribute on the 'ids-icon' element, if present
    const idsIcon = this.querySelector('ids-icon');
    if (idsIcon && isCompact) {
      idsIcon.setAttribute(attributes.COMPACT, 'true');
    } else {
      idsIcon?.removeAttribute(attributes.COMPACT);
    }

    // Apply the compact class
    this.#setCompactClass(isCompact);
  }

  /**
   * Indicates whether the button is in a compact state.
   * @returns {boolean | string} True if the button has the `compact` attribute, otherwise false.
   */
  get compact(): boolean | string {
    return this.state.compact;
  }

  /**
   * Set the button content alignment between 'default (center)', 'start', or 'end'
   * @param {IdsButtonContentAlignment | null} val a valid button content alignment strategy
   */
  set contentAlign(val: IdsButtonContentAlignment | null) {
    if (!val || BUTTON_CONTENT_ALIGNMENTS.indexOf(val) <= 0) {
      this.removeAttribute(attributes.CONTENT_ALIGN);
      this.state.contentAlign = BUTTON_CONTENT_ALIGNMENTS[0];
    } else {
      this.setAttribute(attributes.CONTENT_ALIGN, val);
      if (this.state.contentAlign !== val) this.state.contentAlign = val;
    }
    this.setContentAlignClass(val);
  }

  /**
   * @returns {IdsButtonContentAlignment} the currently set content alignment
   */
  get contentAlign(): IdsButtonContentAlignment {
    return this.state.contentAlign;
  }

  /**
   * Set how content overflows; can specify 'ellipsis', or undefined or 'none'
   * @param {string | null} value how content is overflow
   */
  set overflow(value: string | null) {
    const isEllipsis = value === 'ellipsis';

    if (isEllipsis) {
      this.container?.classList.add('ellipsis');
      this.setAttribute('overflow', 'ellipsis');
    } else {
      this.container?.classList.remove('ellipsis');
      this.removeAttribute('overflow');
    }
  }

  get overflow(): string | null {
    return this.getAttribute('overflow');
  }

  /**
   * Sets the correct content alignment class on the ShadowRoot button.
   * @private
   * @param {string | null} val desired content alignment class
   */
  setContentAlignClass(val: string | null) {
    if (this.button) {
      BUTTON_CONTENT_ALIGNMENTS.forEach((app) => {
        const appClassName = `content-align-${app}`;
        if (val === app) {
          if (app !== 'default' && !this.button?.classList.contains(appClassName)) {
            this.button?.classList.add(appClassName);
          }
          return;
        }
        if (this.button?.classList.contains(appClassName)) {
          this.button?.classList.remove(appClassName);
        }
      });
    }
  }

  /**
   * @param {Array<string>|string} val containing CSS classes that will be applied to the button
   * Strings will be split into an array and separated by whitespace.
   */
  set cssClass(val: Array<string> | string) {
    let attr = val;
    let newCl: any[] = [];
    // @TODO replace with clone utils method
    const prevClasses = [].concat(this.state.cssClass);

    if (Array.isArray(val)) {
      newCl = val;
      attr = val.join(' ');
    } else if (typeof val === 'string' && val.length) {
      newCl = val.split(' ');
    }

    this.state.cssClass = newCl;
    if (newCl.length) {
      this.setAttribute(attributes.CSS_CLASS, attr.toString());
    } else {
      this.removeAttribute(attributes.CSS_CLASS);
    }

    // Remove/Set CSS classes on the actual inner Button component
    if (!this.button) return;

    const buttonCl = this.button.classList;
    const buttonClArr = Array.from(buttonCl);
    prevClasses.forEach((cssClass) => {
      if (!newCl.includes(cssClass)) {
        buttonCl.remove(cssClass);
      }
    });
    newCl.forEach((newCssClass) => {
      if (!buttonClArr.includes(newCssClass)) {
        buttonCl.add(newCssClass);
      }
    });
  }

  get cssClass(): string {
    return this.state.cssClass;
  }

  /**
   * Passes a disabled attribute from the custom element to the button
   * @param {boolean | string} val true if the button will be disabled
   */
  set disabled(val: boolean | string) {
    const isValueTruthy = stringToBool(val);
    this.shouldUpdate = false;
    if (isValueTruthy) {
      this.setAttribute(attributes.DISABLED, '');
    } else {
      this.removeAttribute(attributes.DISABLED);
    }

    this.shouldUpdate = true;
    this.state.disabled = isValueTruthy;
    if (this.button) this.button.disabled = isValueTruthy;
  }

  get disabled(): boolean | string {
    return this.state.disabled;
  }

  /**
   * Passes a tabIndex attribute from the custom element to the button
   * @param {number | string | null} val the tabIndex value
   */
  set #tabIndex(val: number | string | null) {
    const trueVal = Number(val);

    this.removeAttribute(attributes.TABINDEX);

    if (Number.isNaN(trueVal) || trueVal < -1) {
      this.state.tabIndex = 0;
      this.button?.setAttribute(attributes.TABINDEX, '0');
      return;
    }

    this.state.tabIndex = trueVal;
    this.button?.setAttribute(attributes.TABINDEX, `${trueVal}`);
  }

  /**
   * @returns {number} the current tabIndex number for the button
   */
  get #tabIndex(): number {
    return this.state.tabIndex;
  }

  /**
   * Sets the icon on the button
   * @param {string | undefined} val representing the icon to set
   */
  set icon(val) {
    if (typeof val !== 'string' || !val.length) {
      this.removeAttribute(attributes.ICON);
      this.state.icon = undefined;
      this.removeIcon();
      return;
    }
    this.state.icon = val;
    this.setAttribute(attributes.ICON, val);
    this.appendIcon(val);
  }

  /**
   * Gets the current icon used on the button
   * @returns {string} a defined IdsIcon's `icon` attribute, if one is present
   */
  get icon(): string | undefined | null {
    return this.iconEl?.getAttribute('icon');
  }

  /**
   * Gets the current icon element
   * @readonly
   * @returns {HTMLElement | null} a defined IdsIcon, if one is present
   */
  get iconEl(): HTMLElement | null {
    return this.querySelector('ids-icon');
  }

  /**
   * Sets the automatic alignment of an existing icon to the 'start' or 'end' of the text
   * @param {IdsButtonIconAlignment} val automatic icon alignment setting, if applicable (defaults to undefined).
   */
  set iconAlign(val: IdsButtonIconAlignment) {
    if (!ICON_ALIGN_CLASSNAMES.includes(`align-icon-${val}`)) {
      val = undefined;
    }
    this.state.iconAlign = val;
    this.setIconAlignment();
  }

  /**
   * @returns {IdsButtonIconAlignment} automatic icon alignment setting, if enabled
   */
  get iconAlign(): IdsButtonIconAlignment {
    return this.state?.iconAlign;
  }

  /**
   * Get width
   * @returns {string | null} 100%, 90px, 50rem etc.
   */
  get width(): string | null {
    return this.getAttribute(attributes.WIDTH);
  }

  /**
   * Set width of button
   * @param {string | null} w 100%, 90px, 50rem etc.
   */
  set width(w: string | null) {
    if (!w) {
      this.removeAttribute(attributes.WIDTH);
      this.style.width = '';
      if (this.button) this.button.style.width = '';
      return;
    }

    // if percentage passed set width to host
    if (w.indexOf('%') !== -1) {
      this.style.width = w;
      if (this.button) this.button.style.width = '';
    } else {
      this.style.width = '';
      if (this.button) this.button.style.width = w;
    }

    this.setAttribute(attributes.WIDTH, w);
  }

  /**
   * Get the height attribute
   * @returns {string | null} 100%, 90px, 50rem etc.
   */
  get height(): string | null {
    return this.getAttribute(attributes.HEIGHT);
  }

  /**
   * Set height of button
   * @param {string | null} value 100%, 90px, 50rem etc.
   */
  set height(value: string | null) {
    if (!value) {
      this.removeAttribute(attributes.HEIGHT);
      this.style.height = '';
      if (this.button) this.button.style.height = '';
      return;
    }

    // if percentage passed set height to host
    if (value.indexOf('%') !== -1) {
      this.style.height = value;
      if (this.button) this.button.style.height = '';
    } else {
      this.style.height = '';
      if (this.button) this.button.style.height = value;
    }

    this.setAttribute(attributes.HEIGHT, value);
  }

  /**
   * Sets flag for generative AI animation
   * @param {boolean | string} val animation flag
   */
  set noAnimation(val: boolean | string) {
    const skipAnimation = stringToBool(val);
    this.toggleAttribute(attributes.NO_ANIMATION, skipAnimation);
    this.container?.classList.toggle(attributes.NO_ANIMATION, skipAnimation);
  }

  /**
   * Gets flag for generative AI animation
   * @returns {boolean} animation flag
   */
  get noAnimation(): boolean | string {
    return this.hasAttribute(attributes.NO_ANIMATION);
  }

  /**
   * Check if an icon exists, and adds the icon if it's missing
   * @param {string} iconName The icon name to check
   * @private
   */
  appendIcon(iconName: string) {
    const icon = this.querySelector<IdsIcon>(`ids-icon`); // @TODO check for dropdown/expander icons here
    const align = this.iconAlign;
    const iconInsertTarget = align === 'end' ? 'beforeend' : 'afterbegin';

    if (icon) {
      if (icon.icon !== iconName) {
        icon.icon = iconName;
        this.setIconAlignment();
      }
      if (align === 'end' && this.hasIncorrectEndElement()) this.append(icon);
      else if (this.hasIncorrectStartElement()) this.prepend(icon);
    } else {
      this.insertAdjacentHTML(iconInsertTarget, `<ids-icon icon="${iconName}" class="ids-icon"></ids-icon>`);
    }

    this.refreshProtoClasses();
  }

  /**
   * Check if an icon exists, and removes the icon if it's present
   * @private
   */
  removeIcon(): void {
    const icon = this.querySelector(`ids-icon`); // @TODO check for dropdown/expander icons here

    if (icon) icon.remove();
    this.setIconAlignment();
    this.refreshProtoClasses();
  }

  /**
   * Adds/Removes Icon Alignment CSS classes to/from the inner button component.
   * @private
   */
  private setIconAlignment(): void {
    if (!this.button) return;

    const alignment = this.iconAlign;
    const iconStr = this.icon;
    this.button.classList.remove(...ICON_ALIGN_CLASSNAMES);

    // Align and append the icon, if needed
    if (alignment) {
      if (iconStr) {
        this.button.classList.add(`align-icon-${alignment}`);
        const incorrectStartElement = this.hasIncorrectStartElement();
        const incorrectEndElement = this.hasIncorrectEndElement();
        if (incorrectStartElement || incorrectEndElement) this.appendIcon(iconStr);
      }
    }
  }

  /**
   * @returns {boolean} true if an icon element isn't the first child element of this button
   */
  private hasIncorrectStartElement() {
    return this.iconAlign === 'start' && !this.children[0].isEqualNode(this.iconEl);
  }

  /**
   * @returns {boolean} true if an icon element isn't the last child element of this button
   */
  private hasIncorrectEndElement() {
    return this.iconAlign === 'end' && !this.children[this.children.length - 1].isEqualNode(this.iconEl);
  }

  /**
   * @param {string | null} val the text value
   * @returns {void}
   */
  set text(val: string | null) {
    this.removeAttribute(attributes.TEXT);

    if (typeof val !== 'string' || !val.length) {
      this.state.text = '';
      this.removeText();
      return;
    }

    // @TODO: Run this through an XSS check
    this.state.text = val;
    this.appendText(val);
  }

  /**
   * @returns {string | null} the current text value
   */
  get text(): string | null {
    const textElem = this.querySelector('span:not(.audible)');
    if (textElem && textElem.textContent?.length) {
      return textElem.textContent;
    }
    return this.textContent;
  }

  /**
   * Check if the text slot exists, and appends it if it's missing
   * @param {string} val New text contents
   * @private
   */
  private appendText(val: string) {
    const text = this.querySelector(`span:not(.audible)`) || this.querySelector('ids-text');
    const align = this.iconAlign;
    const iconInsertTarget = align === 'end' ? 'afterbegin' : 'beforeend';

    if (text) {
      text.textContent = val;
      this.setAriaText();
      if (align === 'end' && this.hasIncorrectEndElement()) this.prepend(text);
      else if (align === 'start' && this.hasIncorrectStartElement()) this.append(text);
    } else {
      this.insertAdjacentHTML(iconInsertTarget, `<span>${val}</span>`);
    }
    this.refreshProtoClasses();
  }

  /**
   * Checks if the text slot exists, and removes it if necessary
   * @private
   */
  private removeText() {
    const text = this.querySelector(`span:not(.audible)`) || this.querySelector('ids-text');
    if (text) {
      text.remove();
    }
    this.refreshProtoClasses();
  }

  #setupGenAIButton(appearance: string) {
    if (!appearance.includes('generative')) return;

    if (!this.iconAlign) {
      this.state.iconAlign = 'start';
      this.setAttribute(attributes.ICON_ALIGN, 'start');
    }

    const linerGradient = `<defs>
      <linearGradient id="paint-linear" x1="14.6681" y1="-32.5625" x2="31.7256" y2="-26.8896" gradientUnits="userSpaceOnUse">
        <stop offset="0.117757" class="insights-stop-1"></stop>
        <stop offset="0.927091" class="insights-stop-2"></stop>
      </linearGradient>
    </defs>`;
    const activeAnimation = `<div class="loading-dots loading-dots-${this.iconAlign || 'start'}">
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
    </div>`;

    // Add genai icon if no icon exists
    let genaiIcon = this.querySelector('ids-icon[icon="genai"]') as IdsIcon;
    if (!this.querySelector('ids-icon')) {
      const insightsIcon = document.createElement('ids-icon') as IdsIcon;
      insightsIcon.setAttribute('icon', 'genai');
      if (this.iconAlign === 'end') {
        this.append(insightsIcon);
      } else {
        this.prepend(insightsIcon);
      }
      genaiIcon = insightsIcon;
    }

    if (this.iconAlign === 'end') {
      this.container?.insertAdjacentHTML('beforeend', activeAnimation);
    } else {
      this.container?.insertAdjacentHTML('afterbegin', activeAnimation);
    }

    this.#setInitialState();

    // Tertiary style requires svg definitions for gradient colors
    if (appearance.includes('tertiary') && genaiIcon) {
      requestAnimationFrame(() => {
        genaiIcon.appendSVGDefs?.(linerGradient);
        genaiIcon.pathElem?.setAttribute('fill', `url(#paint-linear)`);
      });
    }
  }

  /**
   * Set the button appearance between 'default', 'primary', 'secondary', 'tertiary', or 'destructive'
   * @param {IdsButtonAppearance | null} val a valid button "appearance"
   */
  set appearance(val: IdsButtonAppearance | null) {
    if (!val || BUTTON_APPEARANCE.indexOf(val) <= 0) {
      this.state.appearance = BUTTON_APPEARANCE[0];
      this.removeAttribute(attributes.APPEARANCE);
    } else {
      this.state.appearance = val;
      this.setAttribute(attributes.APPEARANCE, val);
    }

    this.setAppearanceClass(val);
  }

  /**
   * @returns {IdsButtonAppearance} the currently set appearance
   */
  get appearance(): IdsButtonAppearance {
    return this.state.appearance;
  }

  /**
   * Sets the HTMLButtonElement 'type' attribute
   */
  set type(val: IdsButtonType) {
    if (val && BUTTON_TYPES.includes(val)) {
      this.setAttribute(attributes.TYPE, val);
      this.button?.setAttribute(attributes.TYPE, val);
      if (this.state.type !== val) this.state.type = val;
    } else {
      this.removeAttribute(attributes.TYPE);
      this.button?.removeAttribute(attributes.TYPE);
      this.state.type = BUTTON_TYPES[0];
    }
  }

  /**
   * @returns {IdsButtonType} Gets the HTMLButtonElement 'type' attribute
   */
  get type(): IdsButtonType {
    return this.state.type || 'button';
  }

  /**
   * Sets the no margins attribute
   * @param {boolean | string} n boolean value for the no margins attribute
   */
  set noMargins(n: boolean | string) {
    if (stringToBool(n)) {
      this.setAttribute(attributes.NO_MARGINS, '');
      this.container?.classList.add(attributes.NO_MARGINS);
      return;
    }
    this.removeAttribute(attributes.NO_MARGINS);
    this.container?.classList.remove(attributes.NO_MARGINS);
  }

  get noMargins(): boolean | string {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  /**
   * Sets the margin-block-end on the button
   * @param {string | null} value CSS length value (e.g. '8px', '1rem')
   */
  set marginBlockEnd(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MARGIN_BLOCK_END, value);
    } else {
      this.removeAttribute(attributes.MARGIN_BLOCK_END);
    }

    if (this.container) {
      this.container.style.marginBlockEnd = value || '';
    }
  }

  /**
   * @returns {string | null} the current margin-block-end value
   */
  get marginBlockEnd(): string | null {
    return this.getAttribute(attributes.MARGIN_BLOCK_END);
  }

  /**
   * @param {boolean | string} val true if the button should not have standard padding rules applied
   */
  set noPadding(val: boolean | string) {
    const isTruthy = this.noPadding;
    const trueVal = stringToBool(val);
    if (isTruthy !== trueVal) {
      if (trueVal) {
        this.container?.classList.add(attributes.NO_PADDING);
        this.setAttribute(attributes.NO_PADDING, 'true');
      } else {
        this.container?.classList.remove(attributes.NO_PADDING);
        this.removeAttribute(attributes.NO_PADDING);
      }
    }
  }

  /**
   * @returns {boolean | string} true if the button does not currently have standard padding rules applied
   */
  get noPadding(): boolean | string {
    return stringToBool(this.getAttribute(attributes.NO_PADDING));
  }

  /**
   * @param {boolean} value whether the corners of the button as an icon-button should be angled/90°
   */
  set square(value: boolean) {
    const isTruthy = stringToBool(value);

    if (this.button) {
      if (isTruthy && !this.button.classList.contains('square')) {
        this.button.classList.add('square');
      } else if (!isTruthy && this.button.classList.contains('square')) {
        this.button.classList.remove('square');
      }
    }

    if (isTruthy && !this.hasAttribute(attributes.SQUARE)) {
      this.setAttribute(attributes.SQUARE, '');
    } else if (!isTruthy && this.hasAttribute(attributes.SQUARE)) {
      this.removeAttribute(attributes.SQUARE);
    }
  }

  /**
   * @returns {boolean} whether the corners of the button as an icon-button are angled/90°
   */
  get square(): boolean {
    return this.hasAttribute(attributes.SQUARE);
  }

  /**
   * Set the background fill for icon buttons.
   * Only applies to icon button variant.
   * @param {boolean} value when true, applies a solid background using color/background/default
   * to ensure visibility on complex backgrounds such as images.
   */
  set backgroundFill(value: boolean) {
    const isTruthy = stringToBool(value);

    if (this.button) {
      if (isTruthy && !this.button.classList.contains('background-fill')) {
        this.button.classList.add('background-fill');
      } else if (!isTruthy && this.button.classList.contains('background-fill')) {
        this.button.classList.remove('background-fill');
      }
    }

    if (isTruthy && !this.hasAttribute(attributes.BACKGROUND_FILL)) {
      this.setAttribute(attributes.BACKGROUND_FILL, '');
    } else if (!isTruthy && this.hasAttribute(attributes.BACKGROUND_FILL)) {
      this.removeAttribute(attributes.BACKGROUND_FILL);
    }
  }

  /**
   * Get the background fill setting
   * @returns {boolean} true if background fill is enabled
   */
  get backgroundFill(): boolean {
    return this.hasAttribute(attributes.BACKGROUND_FILL);
  }

  /**
   * Sets the correct appearance class on the ShadowRoot button.
   * @private
   * @param {string | null} val desired appearance class
   */
  private setAppearanceClass(val: string | null) {
    if (this.button) {
      BUTTON_APPEARANCE.forEach((app) => {
        const appClassName = `btn-${app}`;
        if (val === app) {
          if (app !== 'default' && !this.button?.classList.contains(appClassName)) {
            this.button?.classList.add(appClassName);
          }
          return;
        }
        if (this.button?.classList.contains(appClassName)) {
          this.button?.classList.remove(appClassName);
        }
      });
      this.button?.classList.remove('btn-undefined');
    }
  }

  /**
   * Adds or removes the `compact` CSS class on the button's shadowRoot.
   * This class is used to apply compact-specific styling.
   * @private
   * @param {boolean} isCompact `true` to add the class, `false` to remove it.
   */
  #setCompactClass(isCompact: boolean | null) {
    this.container?.classList.toggle('compact', !!isCompact);
  }

  /**
   * Checks if the provided value is valid.
   * @param {boolean|string|null} value - The value to check.
   * @returns {boolean} True if the value is valid, otherwise false.
   */
  #isValidValue(value: boolean | string | null): boolean {
    const validValues = new Set([true, 'true', false, 'false', null, 'null']);
    return validValues.has(value);
  }

  /**
   * Overrides the standard "focus" behavior to instead pass focus to the inner HTMLButton element.
   */
  focus(): void {
    this.button?.focus();
  }

  /**
   * Updates the label text for the button component.
   * Manages the creation, update, and removal of label elements and their container.
   * @param {string} value label text
   * @returns {void}
   */
  setLabelText(value: string): void {
    // Only apply label container logic for ids-button, not for extended components like ids-trigger-button
    if (this.tagName.toLowerCase() !== 'ids-button') return;
    if (!this.shadowRoot || !this.button) return;

    const container = this.shadowRoot.querySelector('.container');
    const label = container?.querySelector('label');
    const labelText = label?.querySelector('ids-text');

    // Handle truthy value: update existing label or create new container
    if (value) {
      if (labelText) {
        // Update existing label text
        labelText.textContent = value;
      } else {
        // Create container and add label
        const newContainer = document.createElement('div');
        newContainer.className = 'container has-label';
        newContainer.setAttribute('part', 'container');
        newContainer.innerHTML = `<label class="ids-label-text" part="label"><ids-text part="label" color-unset>${escapeHTML(value)}</ids-text></label>`;
        this.button.before(newContainer);
        newContainer.appendChild(this.button);
      }
      return;
    }

    if (container) {
      label?.remove();
      container.classList.remove('has-label');
      container.before(this.button);
      container.remove();
    }
  }

  /**
   * Implements callback from IdsColorVariantMixin used to
   * update the color variant on children components
   * @returns {void}
   */
  onColorVariantRefresh(): void {
    const icons = this.querySelectorAll<IdsIcon>('ids-icon');
    const texts = this.querySelectorAll<IdsText | HTMLSpanElement>('ids-text, span');
    const iterator = (el: IdsIcon | IdsText | HTMLSpanElement) => {
      if (!(el instanceof HTMLSpanElement)) el.colorVariant = this.colorVariant;
    };
    [...icons, ...texts].forEach(iterator);
  }

  /**
   * @param {IdsColorValueStatus | null} value sets the color of the notification badge
   */
  set badgeColor(value: IdsColorValueStatus | null) {
    if (value) {
      this.setAttribute(attributes.BADGE_COLOR, value);
      this.iconEl?.setAttribute(attributes.BADGE_COLOR, value);
    } else {
      this.removeAttribute(attributes.BADGE_COLOR);
      this.iconEl?.removeAttribute(attributes.BADGE_COLOR);
    }
  }

  get badgeColor(): IdsColorValueStatus | null | undefined {
    return this.iconEl?.getAttribute(attributes.BADGE_COLOR) as IdsColorValueStatus;
  }

  /**
   * @returns {string | null} position of notification badge
   */
  get badgePosition(): string | null | undefined {
    return this.iconEl?.getAttribute(attributes.BADGE_POSITION);
  }

  /**
   * @param {string} value sets the postion of the notification badge
   */
  set badgePosition(value: string | null) {
    if (value) {
      this.setAttribute(attributes.BADGE_POSITION, value);
      this.iconEl?.setAttribute(attributes.BADGE_POSITION, value);
    } else {
      this.removeAttribute(attributes.BADGE_POSITION);
      this.iconEl?.removeAttribute(attributes.BADGE_POSITION);
    }
  }

  /**
   * Set the audible text of button
   * @param {string} value the audible text value
   */
  set labelAudible(value: string) {
    if (value.length > 0) {
      this.setAttribute(attributes.LABEL_AUDIBLE, value);
      this.setAudibleText();
    } else {
      this.removeAttribute(attributes.LABEL_AUDIBLE);
    }
  }

  get labelAudible(): string | null {
    return this.getAttribute(attributes.LABEL_AUDIBLE) || null;
  }

  /**
   * Aria describedby for button
   * @param {string} value the aria describedby id value
   */
  set describedBy(value: string) {
    if (this.container) {
      if (value.length > 0) {
        this.setAttribute(attributes.DESCRIBED_BY, value);
        this.container.setAttribute(htmlAttributes.ARIA_DESCRIBED_BY, value);
      } else {
        this.removeAttribute(attributes.DESCRIBED_BY);
        this.container.removeAttribute(htmlAttributes.ARIA_DESCRIBED_BY);
      }
    }
  }

  get describedBy(): string {
    return this.getAttribute(attributes.DESCRIBED_BY) || '';
  }

  /**
   * Determines whether the tooltip should be shown based on overflow state and content
   * This is used by ids-tooltip-mixin
   * @private
   * @returns {boolean} true if tooltip should be shown, false otherwise
   */
  canTooltipShow() {
    if (this.overflow !== 'ellipsis') return true;
    this.button?.classList.remove('ellipsis');
    if (this.tooltip && this.tooltip !== 'false' && this.button) {
      if (checkOverflow(this.button) || checkOverflow(this.span)) {
        this.button.classList.add('ellipsis');
        return true;
      }
    }
    return false;
  }

  /**
   * Handle inert attribute changes
   * @private
   */
  #handleInertAttribute() {
    if (this.hasAttribute(attributes.INERT)) {
      this.#storedAriaLabel = this.button?.getAttribute(htmlAttributes.ARIA_LABEL) || '';
      this.button?.removeAttribute(htmlAttributes.ARIA_LABEL);
      this.button?.setAttribute(htmlAttributes.TABINDEX, '-1');
      this.button?.setAttribute(htmlAttributes.ROLE, 'none');
    } else {
      const hasTabIndexValue = this.tabIndex !== null && this.tabIndex !== undefined;
      if (this.#storedAriaLabel) {
        this.button?.setAttribute(htmlAttributes.ARIA_LABEL, this.#storedAriaLabel);
      } else {
        this.setAriaText();
      }
      this.button?.removeAttribute(htmlAttributes.ROLE);
      this.button?.setAttribute(htmlAttributes.TABINDEX, `${hasTabIndexValue ? this.tabIndex : '0'}`);
    }
  }

  /**
   * Stored aria-label value for restoration
   * @private
   */
  #storedAriaLabel = '';

  /**
   * Set the inert attribute
   * @param {boolean | string} value true to make button inert
   */
  set inert(value: boolean | string) {
    const isInert = stringToBool(value);
    if (isInert) {
      this.setAttribute(attributes.INERT, '');
    } else {
      this.removeAttribute(attributes.INERT);
    }
    this.#handleInertAttribute();
  }

  /**
   * Get the inert state
   * @returns {boolean} true if button is inert
   */
  get inert(): boolean {
    return this.hasAttribute(attributes.INERT);
  }
}
